// Wählton („wie Close“): Der Anrufer hört durchgehend einen Ton, bis der Angerufene abnimmt oder auflegt.
// Zehn sanfte, selbst erzeugte Töne (WebAudio, keine Dateien): „Weich“ (Standard), „Glocke“, „Puls“, „Melodie“, „Kalimba“,
// „Harfe“, „Sanfte Welle“, „Windspiel“, „Morgen“, „Tropfen“. Sie laufen NUR im Power Dialer (100 bis 200 Anrufe am Tag).
// Normale Anrufe (Lead, Liste, Inbox, Rückruf, Nummer wählen) haben keinen lokalen Ton: dort kommt das Netz-Freizeichen.
// „Netz-Freizeichen“ = der klassische Weg: das echte Freizeichen aus dem Netz (Twilio EarlyMedia bzw. answerOnBridge);
// kommt davon nichts an, läuft als Ersatz das deutsche Freizeichen (425 Hz, 1 s an, 4 s aus). „Aus“ = nichts.
// Nie beides gleichzeitig: Bei einem eigenen Ton wird das Netz-Piepen stumm geschaltet (`netztonStumm`) und, sobald der
// Server Early Media abschalten kann, gar nicht erst angefordert. Bei „Netz“ wird der Ersatzton ausgeblendet, sobald
// echter Ton auf der Leitung ankommt.
//
// Lautstärke: der Regler „Lautstärke der Töne“ (signalton.ts, Standard 0,5) skaliert auch diesen Ton.
//
// Dieser Teil ist reine Logik (ohne Browser-Zugriff testbar); die WebAudio-Ausgabe steht unten in `Rufton`.

export const RUFTON_HZ = 425;
export const RUFTON_AN_MS = 1000;
export const RUFTON_AUS_MS = 4000;
/** Leise genug, um neben dem Gespräch nicht zu stören; das echte Freizeichen im Netz liegt deutlich darüber. */
export const RUFTON_LAUTSTAERKE = 0.06;
export const RUFTON_FLANKE_MS = 20;
/** Lautstärke des Gegenstellen-Audios (SDK „volume“, 0 bis 1), ab der echter Ton auf der Leitung angenommen wird */
export const ECHTTON_SCHWELLE = 0.012;
/** So viele Messungen in Folge müssen über der Schwelle liegen (ein Knacken zählt nicht) */
export const ECHTTON_PROBEN = 3;

/**
 * Erkennt, ob auf der Leitung echter Ton vom Netz ankommt (Freizeichen aus der Telefonanlage des Angerufenen).
 * Rastet ein: einmal erkannt, bleibt es für diesen Anruf erkannt.
 */
export class EchtTonErkennung {
  private folge = 0;
  erkannt = false;

  /** Ein Messwert der Gegenstelle (0 bis 1). Liefert true, sobald (und solange) echter Ton erkannt ist. */
  probe(aus: number): boolean {
    if (this.erkannt) return true;
    if (Number.isFinite(aus) && aus >= ECHTTON_SCHWELLE) this.folge += 1;
    else this.folge = 0;
    if (this.folge >= ECHTTON_PROBEN) this.erkannt = true;
    return this.erkannt;
  }

  zuruecksetzen(): void {
    this.folge = 0;
    this.erkannt = false;
  }
}

export interface RuftonLage {
  /** Der Nutzer hat den Rufton nicht abgeschaltet (Einstellung) */
  erlaubt: boolean;
  /** Ein ausgehender Anruf ist angelegt, nicht eingehend */
  ausgehend: boolean;
  /** Direkt verbinden (<Dial><Number>): Die Browserleitung gilt erst bei Abheben als angenommen */
  direkt: boolean;
  /** Konferenzmodus: Die Browserleitung steht (Twilio „accept“) */
  leitungOffen: boolean;
  /** Direktmodus: Twilio meldet „ringing“; false heißt „kein Netz-Freizeichen“, true heißt „Freizeichen kommt vom Netz“ */
  klingeltOhneNetzton: boolean;
  /** Der Angerufene hat abgenommen (Server „in-progress“) bzw. das Gespräch läuft */
  imGespraech: boolean;
  /** Der Anruf ist beendet, abgebrochen oder gescheitert */
  beendet: boolean;
  /** Echter Ton kam schon an */
  echterTon: boolean;
  /** Eigener sanfter Ton gewählt (Weich, Glocke, Puls): läuft unabhängig vom Netz-Freizeichen, das stumm geschaltet ist */
  eigenerTon?: boolean;
  /** Direktmodus: Twilio hat „ringing“ gemeldet (egal ob mit oder ohne Netz-Freizeichen) */
  klingelt?: boolean;
}

/** Soll der lokale Rufton jetzt laufen? Eine einzige Stelle für alle Stopp-Gründe. */
export function ruftonSoll(l: RuftonLage): boolean {
  if (!l.erlaubt || !l.ausgehend || l.imGespraech || l.beendet || l.echterTon) return false;
  if (l.eigenerTon && l.direkt) return !!l.klingelt || l.klingeltOhneNetzton;
  return l.direkt ? l.klingeltOhneNetzton : l.leitungOffen;
}


/** Eine Periode des Netz-Ersatztons in Millisekunden */
export const RUFTON_PERIODE_MS = RUFTON_AN_MS + RUFTON_AUS_MS;

/**
 * Zeitpunkte (Sekunden ab Start, relativ) für `zyklen` Perioden: [anStart, anEnde] je Periode.
 * Der erste Ton beginnt sofort, damit der Anrufer gleich Rückmeldung hat.
 */
export function ruftonTakt(zyklen: number): Array<[number, number]> {
  const ergebnis: Array<[number, number]> = [];
  for (let i = 0; i < zyklen; i += 1) {
    const start = (i * RUFTON_PERIODE_MS) / 1000;
    ergebnis.push([start, start + RUFTON_AN_MS / 1000]);
  }
  return ergebnis;
}

// ---- Die Töne ------------------------------------------------------------------------------------------------

/** Wahl in den Einstellungen: vier eigene Töne, das Netz-Freizeichen oder nichts */
export type EigenerKlang = "weich" | "glocke" | "puls" | "melodie" | "kalimba" | "harfe" | "welle" | "windspiel" | "morgen" | "tropfen";
export type Waehlton = EigenerKlang | "netz" | "aus";
/** Was tatsächlich erzeugt werden kann („netz“ spielt als Ersatz das deutsche Freizeichen) */
export type Klang = EigenerKlang | "freizeichen";

/** Standard im Power Dialer: die ruhige Melodie (seit 19.10.2026; Einzelanrufe haben immer den weichen Freizeichen-Ton) */
export const WAEHLTON_STANDARD: Waehlton = "melodie";
/** Ton bei Einzelanrufen: weiches, warmes Freizeichen im Rhythmus des deutschen Freizeichens (1 s Ton, 4 s Pause) */
export const EINZELANRUF_TON: Waehlton = "weich";

export const WAEHLTON_LISTE: ReadonlyArray<{ id: Waehlton; label: string; hinweis: string }> = [
  { id: "weich", label: "Weich", hinweis: "ein leises, rundes Tuten wie das normale Freizeichen, sanft ein- und ausgeblendet (bei Einzelanrufen immer)" },
  { id: "glocke", label: "Glocke", hinweis: "sehr leiser, kurzer Glockenton alle 2,5 Sekunden" },
  { id: "puls", label: "Puls", hinweis: "ganz leises, weiches Pulsieren" },
  { id: "melodie", label: "Melodie", hinweis: "vier weiche Marimba-Töne, ganz leise, alle 4 Sekunden (Standard im Power Dialer)" },
  { id: "kalimba", label: "Kalimba", hinweis: "vier helle, kurze Zupftöne, die schnell ausklingen" },
  { id: "harfe", label: "Harfe", hinweis: "ein ruhiger Akkord, von unten nach oben gezupft" },
  { id: "welle", label: "Sanfte Welle", hinweis: "ein langer, weich an- und abschwellender Klang" },
  { id: "windspiel", label: "Windspiel", hinweis: "hohe, zarte Klänge in lockerem Abstand" },
  { id: "morgen", label: "Morgen", hinweis: "vier aufsteigende Töne, die langsam einsetzen" },
  { id: "tropfen", label: "Tropfen", hinweis: "zwei weiche, runde Tropfen" },
  { id: "netz", label: "Netz-Freizeichen", hinweis: "das Freizeichen aus dem Telefonnetz (das klassische Piepen)" },
  { id: "aus", label: "Aus", hinweis: "kein Ton beim Wählen" },
];

/** Ein Teilton: Frequenz, Anteil an der Gesamtlautstärke (Summe der Anteile = 1) und optional Abklingzeit (Glocke) */
export interface Teilton {
  hz: number;
  anteil: number;
  /** Zeitkonstante des Abklingens in ms; ohne Angabe bleibt der Teilton bis zum Ausblenden gleich laut */
  tauMs?: number;
}
export interface Schlag {
  /** Beginn innerhalb der Periode */
  startMs: number;
  dauerMs: number;
  attackMs: number;
  releaseMs: number;
  teiltoene: Teilton[];
}
export interface KlangDef {
  periodeMs: number;
  /** Höchster Spitzenwert aller Teiltöne zusammen (0 bis 1). Weit unter Sprache, kein Clipping. */
  spitze: number;
  schlaege: Schlag[];
}

/** Anteile auf Summe 1 bringen, damit `spitze` die wirkliche Obergrenze ist */
function normiert(t: Teilton[]): Teilton[] {
  const summe = t.reduce((a, b) => a + b.anteil, 0) || 1;
  return t.map((x) => ({ ...x, anteil: x.anteil / summe }));
}

export const KLAENGE: Record<Klang, KlangDef> = {
  // (a) Weich (19.10.2026, Simon: der Doppelton 330/415 Hz klang wie ein Zughorn): ein einzelner Sinus 425 Hz wie das
  // deutsche Freizeichen, 1 s an, 4 s Pause, 120 ms weich ein- und ausgeblendet
  weich: {
    periodeMs: 5000,
    spitze: 0.06,
    schlaege: [{ startMs: 0, dauerMs: 1000, attackMs: 120, releaseMs: 120, teiltoene: normiert([{ hz: 425, anteil: 1 }]) }],
  },
  // (b) Glocke: weicher Anschlag (20 ms), Obertöne wie bei einer kleinen Glocke, höhere klingen schneller ab, alle 2,5 s
  glocke: {
    periodeMs: 2500,
    spitze: 0.07,
    schlaege: [
      {
        startMs: 0,
        dauerMs: 2000,
        attackMs: 20,
        releaseMs: 400,
        teiltoene: normiert([
          { hz: 523.25, anteil: 1, tauMs: 380 },
          { hz: 1046.5, anteil: 0.35, tauMs: 260 },
          { hz: 1444, anteil: 0.18, tauMs: 170 },
          { hz: 2826, anteil: 0.06, tauMs: 90 },
        ]),
      },
    ],
  },
  // (c) Puls: zwei weiche Pulse (D4 mit leiser Oktave) pro Periode, alles sehr leise
  puls: {
    periodeMs: 3000,
    spitze: 0.045,
    schlaege: [0, 650].map((startMs) => ({
      startMs,
      dauerMs: 480,
      attackMs: 190,
      releaseMs: 290,
      teiltoene: normiert([{ hz: 293.66, anteil: 1 }, { hz: 587.33, anteil: 0.22 }]),
    })),
  },
  // (d) Melodie: vier weiche Töne einer Dur-Pentatonik (G4, C5, E5, D5), Marimba-/Kalimba-artig: Sinus mit 24 ms Anschlag,
  // leisem Oberton (2. und ca. 3,9. Teilton klingen schnell ab) und weichem Abklingen, alle 4 s, sehr leise
  melodie: {
    periodeMs: 4000,
    spitze: 0.045,
    schlaege: [
      { hz: 392, startMs: 0 },
      { hz: 523.25, startMs: 380 },
      { hz: 659.25, startMs: 760 },
      { hz: 587.33, startMs: 1180 },
    ].map(({ hz, startMs }) => ({
      startMs,
      dauerMs: 1000,
      attackMs: 24,
      releaseMs: 300,
      teiltoene: normiert([
        { hz, anteil: 1, tauMs: 280 },
        { hz: hz * 2, anteil: 0.16, tauMs: 130 },
        { hz: hz * 3.9, anteil: 0.05, tauMs: 55 },
      ]),
    })),
  },
  // Kalimba: vier kurze Zupftöne (C5, E5, G5, E5), Metallzunge: leiser Oberton beim 5,4-fachen klingt sofort ab
  kalimba: {
    periodeMs: 4500,
    spitze: 0.04,
    schlaege: [
      { hz: 523.25, startMs: 0 },
      { hz: 659.25, startMs: 340 },
      { hz: 783.99, startMs: 680 },
      { hz: 659.25, startMs: 1100 },
    ].map(({ hz, startMs }) => ({
      startMs,
      dauerMs: 1100,
      attackMs: 24,
      releaseMs: 300,
      teiltoene: normiert([
        { hz, anteil: 1, tauMs: 230 },
        { hz: hz * 5.4, anteil: 0.07, tauMs: 40 },
      ]),
    })),
  },
  // Harfe: Akkord C-Dur von unten nach oben gezupft (C4, E4, G4, C5, E5), je 130 ms später, lang ausklingend
  harfe: {
    periodeMs: 5000,
    spitze: 0.04,
    schlaege: [261.63, 329.63, 392, 523.25, 659.25].map((hz, i) => ({
      startMs: i * 130,
      dauerMs: 1700,
      attackMs: 24,
      releaseMs: 500,
      teiltoene: normiert([
        { hz, anteil: 1, tauMs: 520 },
        { hz: hz * 2, anteil: 0.28, tauMs: 300 },
        { hz: hz * 3, anteil: 0.08, tauMs: 150 },
      ]),
    })),
  },
  // Sanfte Welle: A3 und E4 mit leicht verstimmtem Zwilling (langsames Schweben), 1 s sanft an, 1 s sanft aus
  welle: {
    periodeMs: 5200,
    spitze: 0.05,
    schlaege: [
      {
        startMs: 0,
        dauerMs: 3000,
        attackMs: 1000,
        releaseMs: 1000,
        teiltoene: normiert([{ hz: 220, anteil: 1 }, { hz: 221.5, anteil: 0.8 }, { hz: 329.63, anteil: 0.5 }]),
      },
    ],
  },
  // Windspiel: hohe Pentatonik-Töne in unregelmäßigem Abstand, Röhrenklang (2,76-facher Oberton), sehr leise
  windspiel: {
    periodeMs: 5200,
    spitze: 0.035,
    schlaege: [
      { hz: 1318.5, startMs: 0 },
      { hz: 1046.5, startMs: 280 },
      { hz: 1568, startMs: 720 },
      { hz: 1174.7, startMs: 1030 },
      { hz: 1760, startMs: 1700 },
    ].map(({ hz, startMs }) => ({
      startMs,
      dauerMs: 1900,
      attackMs: 24,
      releaseMs: 500,
      teiltoene: normiert([
        { hz, anteil: 1, tauMs: 460 },
        { hz: hz * 2.76, anteil: 0.1, tauMs: 120 },
      ]),
    })),
  },
  // Morgen: G4, B4, D5, G5 aufsteigend, jeder Ton setzt langsam ein (160 ms) und klingt lange nach
  morgen: {
    periodeMs: 5400,
    spitze: 0.04,
    schlaege: [392, 493.88, 587.33, 783.99].map((hz, i) => ({
      startMs: i * 450,
      dauerMs: 1900,
      attackMs: 160,
      releaseMs: 700,
      teiltoene: normiert([
        { hz, anteil: 1, tauMs: 900 },
        { hz: hz * 2, anteil: 0.12, tauMs: 500 },
      ]),
    })),
  },
  // Tropfen: zwei runde, weiche Tropfen (E5, dann A4), kurzer Anschlag, schnelles Abklingen
  tropfen: {
    periodeMs: 3600,
    spitze: 0.05,
    schlaege: [
      { hz: 659.25, startMs: 0 },
      { hz: 440, startMs: 520 },
    ].map(({ hz, startMs }) => ({
      startMs,
      dauerMs: 700,
      attackMs: 24,
      releaseMs: 260,
      teiltoene: normiert([
        { hz, anteil: 1, tauMs: 130 },
        { hz: hz * 2, anteil: 0.18, tauMs: 70 },
      ]),
    })),
  },
  // Netz-Ersatz: deutsches Freizeichen
  freizeichen: {
    periodeMs: RUFTON_PERIODE_MS,
    spitze: RUFTON_LAUTSTAERKE,
    schlaege: [{ startMs: 0, dauerMs: RUFTON_AN_MS, attackMs: RUFTON_FLANKE_MS, releaseMs: RUFTON_FLANKE_MS, teiltoene: [{ hz: RUFTON_HZ, anteil: 1 }] }],
  },
};

/** Gewählter Ton -> erzeugbarer Klang (`null` bei „aus“) */
export function klangZu(w: Waehlton): Klang | null {
  if (w === "aus") return null;
  return w === "netz" ? "freizeichen" : w;
}

/** Eigener sanfter Ton (also nicht Netz, nicht aus)? Dann wird das Netz-Piepen unterdrückt. */
export function istEigenerTon(w: Waehlton): boolean {
  return w !== "netz" && w !== "aus";
}

/**
 * Hüllkurve für `setValueCurveAtTime` (Werte 0 bis 1): weiches Einblenden (sin²), optional exponentielles Abklingen
 * (`tauMs`), weiches Ausblenden (cos²) in den letzten `releaseMs`. Beginnt und endet bei 0, keine harten Kanten.
 */
export function huellkurve(dauerMs: number, attackMs: number, releaseMs: number, tauMs?: number, punkte = Math.min(1024, Math.max(128, Math.ceil(dauerMs / 3)))): Float32Array {
  const k = new Float32Array(punkte);
  for (let i = 0; i < punkte; i += 1) {
    const t = (i / (punkte - 1)) * dauerMs;
    let w = 1;
    if (attackMs > 0 && t < attackMs) w *= Math.sin((Math.PI / 2) * (t / attackMs)) ** 2;
    if (tauMs) w *= Math.exp(-t / tauMs);
    if (releaseMs > 0 && t > dauerMs - releaseMs) w *= Math.cos((Math.PI / 2) * ((t - (dauerMs - releaseMs)) / releaseMs)) ** 2;
    k[i] = w;
  }
  k[0] = 0;
  k[punkte - 1] = 0;
  return k;
}

/** Zeitpunkte (Sekunden ab Start) aller Schläge für `zyklen` Perioden: [Beginn, Ende] */
export function klangTakt(klang: Klang, zyklen: number): Array<[number, number]> {
  const d = KLAENGE[klang];
  const aus: Array<[number, number]> = [];
  for (let i = 0; i < zyklen; i += 1) {
    for (const s of d.schlaege) {
      const von = (i * d.periodeMs + s.startMs) / 1000;
      aus.push([von, von + s.dauerMs / 1000]);
    }
  }
  return aus;
}

/** Merker am <html> für Tests und Fehlersuche: data-rufton="an" solange der lokale Ton läuft, data-rufton-klang="weich" … */
function markieren(an: boolean, klang?: Klang): void {
  try {
    if (an) {
      document.documentElement.dataset.rufton = "an";
      if (klang) document.documentElement.dataset.ruftonKlang = klang;
    } else {
      delete document.documentElement.dataset.rufton;
      delete document.documentElement.dataset.ruftonKlang;
    }
  } catch {
    /* ohne DOM egal */
  }
}

// ---- Einstellung (pro Browser) -------------------------------------------------------------------------------

const RUFTON_SPEICHER = "crm.telefonie.rufton";

const GUELTIG: ReadonlyArray<Waehlton> = WAEHLTON_LISTE.map((w) => w.id);

/** Gewählter Wählton für den Power Dialer (pro Browser). Standard: „weich“. Alter Wert „aus“ (frühere Checkbox) bleibt „aus“. */
export function waehltonLesen(): Waehlton {
  try {
    const w = window.localStorage.getItem(RUFTON_SPEICHER);
    return GUELTIG.includes(w as Waehlton) ? (w as Waehlton) : WAEHLTON_STANDARD;
  } catch {
    return WAEHLTON_STANDARD;
  }
}

export function waehltonSetzen(w: Waehlton): void {
  try {
    if (w === WAEHLTON_STANDARD) window.localStorage.removeItem(RUFTON_SPEICHER);
    else window.localStorage.setItem(RUFTON_SPEICHER, w);
  } catch {
    /* ohne Speicher gilt der Standard */
  }
}

/**
 * Wählton für „Direkt verbinden“ (<Dial><Number>): Der Server kann das Netz-Freizeichen (Early Media) dort nicht
 * abschalten, der Browser schaltet es bis zum Abheben stumm. Damit nie „Tüüt“ vom Netz kommt, gilt statt „Netz-Freizeichen“
 * der weiche Freizeichen-Ton „weich“; „aus“ bleibt „aus“ (dann ist es still).
 */
export function anrufTon(gewaehlt: Waehlton, dialer: boolean, direkt: boolean): Waehlton {
  if (dialer) return direkt ? direktTon(gewaehlt) : gewaehlt;
  return gewaehlt === "aus" ? "aus" : EINZELANRUF_TON;
}

export function direktTon(w: Waehlton): Waehlton {
  return w === "netz" ? EINZELANRUF_TON : w;
}

/** Läuft überhaupt ein Ton beim Wählen? (alles außer „aus“) */
export function ruftonErlaubt(): boolean {
  return waehltonLesen() !== "aus";
}

/**
 * Netz-Piepen stumm schalten bzw. wieder hörbar machen: die Audiospur der Gegenstelle (SDK `getRemoteStream`) wird
 * abgeschaltet. Das ist die öffentliche Schnittstelle der Twilio-SDK; eine Lautstärke („leiser“) gibt sie für die
 * Gegenstelle nicht her. Gibt zurück, ob eine Spur gefunden und gesetzt wurde.
 */
export function netztonStumm(
  twCall: { getRemoteStream?: () => MediaStream | undefined } | null | undefined,
  stumm: boolean,
): boolean {
  let gesetzt = false;
  try {
    const spuren = twCall?.getRemoteStream?.()?.getAudioTracks?.() ?? [];
    for (const s of spuren) {
      if (s.enabled === stumm) s.enabled = !stumm;
      gesetzt = true;
    }
  } catch {
    /* ohne Spur nichts zu tun */
  }
  try {
    if (stumm && gesetzt) document.documentElement.dataset.netzton = "stumm";
    else delete document.documentElement.dataset.netzton;
  } catch {
    /* ohne DOM egal */
  }
  return gesetzt;
}

/** Lokaler Ton über WebAudio. start/stopp sind idempotent; stopp blendet in 30 ms aus (kein Knacken). */
export class Rufton {
  private ctx: AudioContext | null = null;
  private pegel: GainNode | null = null;
  private audio: (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null = null;
  private nachplaner: number | null = null;
  private planBis = 0;
  private lauf = 0;
  private startend = false;
  private klang: Klang = "weich";
  /** false beim Vorhören: kein Merker am <html> */
  markiert = true;
  /** Faktor auf die Lautstärke (Regler „Lautstärke der Töne“, 1 = Lautstärke bis 19.10.) */
  verstaerkung: () => number = () => 1;
  /** Gibt die Geräte-ID des gewählten Lautsprechers zurück (gleicher wie für das Gespräch), sonst leer */
  ausgangId: () => string | null = () => null;

  get laeuft(): boolean {
    return this.ctx !== null;
  }

  async start(klang: Klang = "freizeichen"): Promise<void> {
    if (this.ctx || this.startend) return;
    this.startend = true;
    this.klang = klang;
    const lauf = ++this.lauf;
    try {
      const ctx = new AudioContext();
      const pegel = ctx.createGain();
      const ziel = ctx.createMediaStreamDestination();
      pegel.gain.value = this.verstaerkung();
      pegel.connect(ziel);
      const audio = new Audio() as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      audio.srcObject = ziel.stream;
      const id = this.ausgangId();
      if (id && audio.setSinkId) await audio.setSinkId(id).catch(() => undefined);
      // Zwischenzeitlich gestoppt (Abheben während des Aufbaus): nichts mehr starten
      if (lauf !== this.lauf) {
        void ctx.close();
        return;
      }
      this.ctx = ctx;
      this.pegel = pegel;
      this.audio = audio;
      this.startend = false;
      void ctx.resume().catch(() => undefined);
      if (this.markiert) markieren(true, klang);
      this.planBis = ctx.currentTime + 0.02;
      this.einplanen();
      // Alle 30 s die nächsten Perioden nachlegen (Klingeldauer ist bis zu mehreren Minuten einstellbar)
      this.nachplaner = window.setInterval(() => this.einplanen(), 30000);
      await audio.play();
    } catch (e) {
      try {
        document.documentElement.dataset.ruftonFehler = String((e as Error)?.message ?? e);
      } catch {
        /* egal */
      }
      this.stopp();
    }
  }

  /** Legt die Schläge der nächsten 60 s fest, ab dem Ende des bisher Eingeplanten. */
  private einplanen(): void {
    const { ctx, pegel } = this;
    if (!ctx || !pegel) return;
    const def = KLAENGE[this.klang];
    const anfang = Math.max(this.planBis, ctx.currentTime);
    const zyklen = Math.ceil(60000 / def.periodeMs);
    for (let i = 0; i < zyklen; i += 1) {
      for (const s of def.schlaege) {
        const t0 = anfang + (i * def.periodeMs + s.startMs) / 1000;
        const dauer = s.dauerMs / 1000;
        for (const teil of s.teiltoene) {
          const osz = ctx.createOscillator();
          const huelle = ctx.createGain();
          osz.type = "sine";
          osz.frequency.value = teil.hz;
          huelle.gain.value = 0;
          huelle.gain.setValueCurveAtTime(
            huellkurve(s.dauerMs, s.attackMs, s.releaseMs, teil.tauMs).map((w) => w * def.spitze * teil.anteil),
            t0,
            dauer,
          );
          osz.connect(huelle).connect(pegel);
          osz.start(t0);
          osz.stop(t0 + dauer + 0.05);
        }
      }
    }
    this.planBis = anfang + (zyklen * def.periodeMs) / 1000;
  }

  stopp(): void {
    this.lauf += 1;
    this.startend = false;
    if (this.nachplaner !== null) {
      window.clearInterval(this.nachplaner);
      this.nachplaner = null;
    }
    const { ctx, pegel, audio } = this;
    this.ctx = null;
    this.pegel = null;
    this.audio = null;
    if (this.markiert) markieren(false);
    if (!ctx) return;
    try {
      if (pegel) {
        pegel.gain.cancelScheduledValues(ctx.currentTime);
        pegel.gain.setValueAtTime(pegel.gain.value, ctx.currentTime);
        pegel.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.03);
      }
    } catch {
      /* egal */
    }
    window.setTimeout(() => {
      try {
        audio?.pause();
        if (audio) audio.srcObject = null;
        void ctx.close();
      } catch {
        /* egal */
      }
    }, 120);
  }
}

/**
 * Vorhören in den Einstellungen: spielt den gewählten Ton einmal an (ca. 5 s) und gibt eine Funktion zum vorzeitigen
 * Stoppen zurück. „Aus“ spielt nichts. Eigene Instanz, setzt nie den Merker `data-rufton` (kein Anruf).
 */
export function waehltonVorhoeren(w: Waehlton, ausgangId: string | null, ende?: () => void, verstaerkung = 1): () => void {
  const klang = klangZu(w);
  if (!klang) {
    ende?.();
    return () => undefined;
  }
  const r = new Rufton();
  r.ausgangId = () => ausgangId;
  r.verstaerkung = () => verstaerkung;
  r.markiert = false;
  let fertig = false;
  const beenden = () => {
    if (fertig) return;
    fertig = true;
    window.clearTimeout(t);
    r.stopp();
    ende?.();
  };
  const t = window.setTimeout(beenden, vorhoerDauerMs(klang));
  void r.start(klang);
  return beenden;
}

/** Dauer des Vorhörens: mindestens eine volle Runde des Klangs plus Ausklingen, höchstens ca. 6 s */
export function vorhoerDauerMs(klang: Klang): number {
  if (klang === "freizeichen") return 2500;
  const d = KLAENGE[klang];
  const ende = Math.max(...d.schlaege.map((s) => s.startMs + s.dauerMs));
  return Math.min(6000, Math.max(4600, ende + 300));
}
